import React, { FC, useEffect, useMemo, useState } from 'react';

import { Typography } from '@mui/material';
import { OrderShort } from '@solidgate/react-sdk';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import 'swiper/swiper.min.css';

import Api from '~/Api';
import Loader from '~/components/atoms/Loader';
import { ModalStateType } from '~/contexts/modal/ModalContext';
import { getProductName } from '~/helpers/translation';
import { useIsViewport, Viewports } from '~/hooks/useIsViewport';
import projectConfig, { PROJECT } from '~/projects/config';
import analytics from '~/services/analytics';
import { useAppSelector } from '~/store/hooks/useAppSelector';
import { Typographies } from '~/theme/typography';
import { AnalyticPaymentMethods, PAYMENT_METHODS } from '~/types/payment';
import { ProductType, REGULARITY } from '~/types/product';

import styles from './styles.module.scss';
import PaymentFormV1 from '../components/PaymentForm/V1';
import Agreement from '../components/PaymentForm/V1/Agreement';
import Guarantee from '../components/PaymentForm/V1/Guarantee';
import OrderDescription from '../components/PaymentForm/V1/OrderDescription';
import PaymentInfo from '../components/PaymentForm/V1/PaymentInfo';
import PaymentSystems from '../components/PaymentForm/V1/PaymentSystems';
import PaymentFormV1Sidekicker from '../components/PaymentForm/V1/sidekicker';

export const paymentFormModalConfig: Partial<ModalStateType> = {
  isFullScreen: false,
  maxWidth: '800px',
  closeButtonColor: '#B3B3B3',
  pageViewMobile: true,
};

export const paymentStripeFormModalConfig: Partial<ModalStateType> = {
  isFullScreen: false,
  maxWidth: '1200px',
  closeButtonColor: '#B3B3B3',
  pageViewMobile: true,
};

type Props = {
  userId: string;
  product: ProductType;
  onSuccessPayment: () => void | Promise<void>;
  onCreateCardIntentError?: (errorMessage: string) => void;
  paymentApi?: {
    createPaymentIntentCard: (
      user_id: string,
      product_id: string,
      geo_country: string,
    ) => Promise<{
      merchant: string;
      payment_intent: string;
      signature: string;
    }>;
    createPaymentIntentPaypal: (
      user_id: string,
      product_id: string,
    ) => Promise<{
      order: Record<string, any>;
      order_metadata: any;
      pay_form: Record<string, any>;
    }>;
  };
  purchaseSource?: string;
};

const Checkout: FC<Props> = ({
  userId,
  product,
  purchaseSource,
  onSuccessPayment,
  onCreateCardIntentError,
  paymentApi = {
    createPaymentIntentCard: Api.createPaymentIntentCard.bind(Api),
    createPaymentIntentPaypal: Api.createPaymentIntentPaypal.bind(Api),
  },
}) => {
  const { t } = useTranslation('general');
  const { email } = useAppSelector((state) => state.profile);
  const [isFormShown, setIsFormShown] = useState(false);

  const isDesktop = useIsViewport(Viewports.EXTRA_SMALL_DESKTOP);

  const handlePaymentSuccess = async (order: OrderShort): Promise<void> => {
    analytics.trackPurchase('platform payment - success', {
      user_id: userId,
      value: order.amount ? order.amount / 100 : 0,
      product_id: product.id,
      product_name: product.name,
      order_id: order.order_id ?? '',
      ...(purchaseSource ? { purchase_source: purchaseSource } : {}),
      currency: order.currency || 'USD',
      email: email,
      transaction_id: product.id,
    });

    await onSuccessPayment();
  };

  const handlePaymentFail = (
    message: string,
    method: AnalyticPaymentMethods,
  ): void => {
    analytics.trackEvent('platform checkout - error', {
      product_id: product.id,
      product_name: product.name,
      method,
      error: message,
      ...(purchaseSource ? { purchase_source: purchaseSource } : {}),
    });
  };

  const handleCheckoutSubmit = (method: AnalyticPaymentMethods): void => {
    analytics.trackEvent('platform checkout - submit', {
      product_id: product.id,
      product_name: product.name,
      method,
      ...(purchaseSource ? { purchase_source: purchaseSource } : {}),
    });
  };

  const handlePaymentMethodChange = (type: PAYMENT_METHODS): void => {
    analytics.trackEvent('platform checkout - navigation', {
      method: type,
      ...(purchaseSource ? { purchase_source: purchaseSource } : {}),
    });
  };

  useEffect(() => {
    analytics.trackEvent('platform checkout - view', {
      user_id: userId,
      product_id: product.id,
      product_name: product.name,
      ...(purchaseSource ? { purchase_source: purchaseSource } : {}),
    });
  }, []);

  const regularityText =
    product.regularity === REGULARITY.ONETIME
      ? t('one_time_payment', 'One time payment')
      : product.has_trial_period
        ? t('general:then_price_per_period_v2', {
            period: t(
              `general:${product.regularity}`,
              product.regularity,
            ).toLowerCase(),
            price: `${product.new_price_ui / 100} ${product.currency}`,
            defaultValue: 'then {{price}} per {{period}}',
          })
        : t('general:per_period_v2', {
            period: t(
              `general:${product.regularity}`,
              product.regularity,
            ).toLowerCase(),
            defaultValue: 'per {{period}}',
          });

  const isSidekicker = projectConfig.project === PROJECT.SIDEKICKER;
  const PaymentFormComponent = isSidekicker
    ? PaymentFormV1Sidekicker
    : PaymentFormV1;

  const memoizedPaymentApi = useMemo(
    () => ({
      createPaymentIntentCard: (geo_country) =>
        paymentApi.createPaymentIntentCard(userId, product.id, geo_country),
      createPaymentIntentPaypal: isSidekicker
        ? undefined
        : () => paymentApi.createPaymentIntentPaypal(userId, product.id),
    }),
    [userId, product.id, isSidekicker],
  );

  return (
    <>
      {!isFormShown && <Loader />}

      <div className={styles.outer_wrapper}>
        <div
          className={clsx(styles.inner_wrapper, {
            [styles.is_shown]: isFormShown,
          })}
        >
          {isDesktop && (
            <div className={styles.description}>
              <OrderDescription
                productId={product.id}
                productName={getProductName(product.id, product.name, t)}
                productRegularity={product.regularity}
                isPopular={product.is_popular_ui}
              />
            </div>
          )}

          <div className={styles.form_outer}>
            <div className={styles.form_inner}>
              {!isSidekicker && (
                <Typography
                  className={styles.title}
                  variant={Typographies.LABEL_LARGE}
                  component="h3"
                >
                  {t(
                    'subscription:payment_form.select_payment_method',
                    'Select your payment method',
                  )}
                </Typography>
              )}

              <PaymentFormComponent
                onPaymentSuccess={handlePaymentSuccess}
                onPaymentFail={handlePaymentFail}
                onPaymentSubmit={handleCheckoutSubmit}
                onPaymentMethodChange={handlePaymentMethodChange}
                onFormReady={() => setIsFormShown(true)}
                onCreateCardIntentError={onCreateCardIntentError}
                paymentApi={memoizedPaymentApi}
              />

              <Guarantee />

              <PaymentInfo
                price={String(
                  ((product.trial_amount || product.price) / 100).toFixed(2),
                )}
                currency={product.currency}
                title={getProductName(product.id, product.name, t)}
                regularity={regularityText}
              />
              <PaymentSystems />
            </div>

            <Agreement />
          </div>
        </div>
      </div>
    </>
  );
};

export default Checkout;
